import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#C84B31", "#4E6C50", "#DAB88B", "#9A8C98", "#81B29A"];

export default function Analytics() {
  const [revenue, setRevenue] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/analytics/revenue", { params: { days: 30 } }).then((r) => setRevenue(r.data));
    api.get("/analytics/top-items", { params: { limit: 8 } }).then((r) => setTopItems(r.data));
    api.get("/analytics/orders-by-type").then((r) => setOrderTypes(r.data));
    api.get("/analytics/summary").then((r) => setSummary(r.data));
  }, []);

  const exportCSV = () => {
    const rows = [["Date", "Revenue", "Orders"], ...revenue.map((r) => [r.date, r.revenue.toFixed(2), r.orders])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "resto-revenue.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        eyebrow="Insights"
        title="Analytics & reports"
        subtitle="Sales trends, best sellers, channel mix and key restaurant operating metrics."
        actions={
          <button onClick={exportCSV} className="btn-primary px-4 py-2 rounded-md text-sm font-semibold" data-testid="export-csv-btn">
            Export CSV
          </button>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total revenue", value: fmtMoney(summary?.total_revenue) },
          { label: "All-time orders", value: summary?.total_orders ?? "—" },
          { label: "Customers", value: summary?.customers ?? "—" },
          { label: "Avg order value", value: fmtMoney(summary?.avg_order_value) },
        ].map((t) => (
          <div key={t.label} className="bento p-5" data-testid={`analytics-tile-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="label-eyebrow">{t.label}</div>
            <div className="font-display font-extrabold text-3xl mt-2">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bento p-5" data-testid="analytics-revenue-line">
          <div className="label-eyebrow">30-day revenue</div>
          <h3 className="font-display font-bold text-xl mb-4">Trend</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={revenue}>
                <CartesianGrid stroke="#EAE7E0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#C84B31" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="orders" stroke="#4E6C50" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bento p-5" data-testid="analytics-top-items">
          <div className="label-eyebrow">Bestsellers</div>
          <h3 className="font-display font-bold text-xl mb-4">Top items</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={topItems} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke="#EAE7E0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="quantity" fill="#C84B31" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bento p-5" data-testid="analytics-order-types">
          <div className="label-eyebrow">Channel mix</div>
          <h3 className="font-display font-bold text-xl mb-4">Orders by type</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={orderTypes} dataKey="count" nameKey="type" outerRadius={90} innerRadius={55} paddingAngle={3}>
                  {orderTypes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bento p-5" data-testid="analytics-tables-revenue">
          <div className="label-eyebrow">Item revenue</div>
          <h3 className="font-display font-bold text-xl mb-4">Revenue per top item</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={topItems}>
                <CartesianGrid stroke="#EAE7E0" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #EAE7E0", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#4E6C50" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
